import { useChannelWithFallback } from '@/api/hooks'
import { getChannelChildren, updateChannelChildren, useChannelChildren } from '@/app/(site)/[username]/channel-children'
import NewCollectionButton from '@/app/components/collections/new-collection-button'
import { fetchChannelChildren } from '@/app/util/channel-api'
import UAParserContext from '@/context/ua-parser'
import UserContext from '@/context/user-context'
import { IChannel } from '@giphy/js-types'
import { Loader } from '@giphy/react-components'
import { useCallback, useContext } from 'react'
import { useInView } from 'react-intersection-observer'
import { desktopContent } from 'ui/src/constants'
import useFetchData from 'ui/src/hooks/use-fetch-data'
import ChannelGridItem from './channel-grid-item'

type Props = { channel: IChannel }

/**
 * A fixed size grid with titles
 */
const ChannelGrid = ({ channel: _channel }: Props) => {
    const { user } = useContext(UserContext)
    const { data: channel = _channel } = useChannelWithFallback(_channel)
    const children = useChannelChildren(channel.id)
    const fetchData = useCallback(
        async (offset: number) => {
            const newKids = await fetchChannelChildren(channel.id, '', {
                offset,
                limit: 12,
            })
            const existingKids = getChannelChildren(channel.id)
            updateChannelChildren(channel.id, [...existingKids, ...newKids.data])
            return newKids
        },
        [channel.id]
    )

    const { deviceType } = useContext(UAParserContext)

    const { ref, inView } = useInView({ rootMargin: '0px 0px 250px 0px' })
    // use this to fetch but rely on Zustand data
    const { doneFetching } = useFetchData<IChannel>({
        initialData: children,
        fetchData,
        triggerFetch: inView,
        checkDone: () => children.length === channel.num_children,
    })
    const { columnWidth, itemHeight } = estimateDimensions(deviceType)
    const canEdit = !!(user && (user.is_staff || user.username === channel.user.username))
    const isSubChannel = channel.ancestors.length > 0

    return (
        <div>
            <div className="grid grid-cols-[1fr_1fr] gap-2 md:grid-cols-[1fr_1fr_1fr]">
                {canEdit && !isSubChannel && (
                    <NewCollectionButton channel={channel} width={columnWidth} height={itemHeight} />
                )}
                {children.map((kid: IChannel) => (
                    <ChannelGridItem channel={kid} key={kid.id} width={columnWidth} height={itemHeight} />
                ))}
            </div>
            {!doneFetching && (
                <div ref={ref}>
                    <Loader />
                </div>
            )}
        </div>
    )
}

export default ChannelGrid

function estimateDimensions(deviceType: string | undefined) {
    const isDesktop = deviceType === 'desktop'
    const columns = isDesktop ? 3 : 2
    const gridWidth = isDesktop ? desktopContent : 340
    const gridSpacing = 12
    const totalGridSpacing = gridSpacing * (columns - 1)
    const columnWidth = (gridWidth - totalGridSpacing) / columns
    const desktopHeight = 138
    const itemHeight = isDesktop ? desktopHeight : (columnWidth * 9) / 16
    return { columnWidth, itemHeight }
}
